import {
    DurationType,
    OracleQuote,
    ExternalYieldVault,
    StrategyDuration,
    TokenListMetadata,
    AssetData
} from "@bridgesplit/abf-sdk";
import { SortDirection } from "@bridgesplit/utils";

import { StrategyAddress } from "./alias";
import { TokenBalanceExpanded } from "./token";
import { MarketInformationAssetData, ParsedMarketInformation } from "./domain";

export type MarketExpanded = {
    usdPrice: number | null;
    principalStats: PrincipalMarketStats;
    strategyStats: PrincipalMarketStrategyStats;
    collateralDepositsUsd: number;
    metadata: TokenListMetadata;
    borrowCap: BorrowCap | null;
    yieldVault: ExternalYieldVault | null;
    collateralYield: number | null;
};

export type MarketDetailStats = {
    principalStats: PrincipalMarketStats;
    strategyStats: PrincipalMarketStrategyStats;
    principalToCollateralDepositedUsd: Record<string, number> | undefined;
    borrowCap: BorrowCap;
};

export interface DurationToMinApyItem {
    duration: number;
    durationType: number;
    apy: number;
}

export interface RawPrincipalMarketStrategyStats {
    totalDeposits: number;
    durationToMinApy: Record<string, number>;
}

export interface PrincipalMarketStrategyStats {
    totalDeposits: number;
    durationToMinApy: DurationToMinApyItem[];
}

export interface MarketStratResponse {
    [marketId: string]: PrincipalMarketStrategyStats;
}
interface DurationToMinApy {
    [key: string]: number;
}

interface TotalDepositsData {
    totalDeposits: number;
    durationToMinApy: DurationToMinApy;
}

export interface RawMarketStratResponse {
    [mint: string]: TotalDepositsData;
}

export interface Collateral extends TokenBalanceExpanded {
    oracle: OracleQuote | null;
    usdPrice: number;
    usdValue: number;
}

export type MaxQuoteFromCollateralAmount = MaxQuote & {
    maxBorrow: number;
};

export interface CollateralWithMaxQuote extends Collateral {
    maxQuote: MaxQuoteFromCollateralAmount | undefined;
}

interface QuoteFilterBase {
    principal: string;
    minPrincipalAmount?: number;
    duration: number;
    durationType: DurationType;
    limit: number;
    offset: number;
    lendingStrategyAddress?: string;
    excluded_external_yield?: number[];
}

export interface CollateralQuoteFilter extends QuoteFilterBase {
    collateral: string[];
}

export interface CollateralQuoteFilterForApy extends CollateralQuoteFilter {
    collateral: string[];
    apyRange: [number, number];
}

export type CollateralFilter = {
    amount: number;
    assetData: AssetData;
};

export interface MaxQuoteRequest {
    collateralFilter: CollateralFilter[];
    principalMint: string;
    duration: number;
    durationType: DurationType;
    strategyCredit?: Record<StrategyAddress, number>;
    excluded_external_yield?: number[];
}

export interface BestQuoteFilter {
    collateralToInfo: Record<string, QuoteCollateralInfo>;
    principalMint: string;
    durations: StrategyDuration[];
    minPrincipalAmount: number;
    showSelf?: boolean;
    excluded_external_yield?: number[];
    // When set, the backend raises min_lqt to this loan's stored floor so sub-floor strategies aren't surfaced.
    loanAddress?: string;
}

export interface QuoteCollateralInfo {
    amount: number;
    assetType: number;
    assetMint: string;
    assetIdentifier: string;
    priceOverride?: number; // ui price override (for when the collateral is not created yet)
}

export interface SellLoanQuote {
    quote: BestSellQuote;
    salePrice: number;
    fairSalePriceAtQuoteRate: number;
    valueLeft: number;
    saleTime: number;
}

// Server caps the batch at 20 loans per request — see MAX_LOANS_PER_SELL_QUOTE
// in markets/src/utils/quotes/raw.rs.
export const MAX_LOANS_PER_SELL_QUOTE_BATCH = 20;

export interface MultiSellLoanQuoteRequest {
    loans: string[];
}

export interface MultiSellLoanQuoteEntry {
    loan: string;
    quote: BestSellQuote | null;
    salePrice: number;
    fairSalePriceAtQuoteRate: number;
    valueLeft: number;
}

export type RawMaxQuote = {
    apy: number;
    strategy: string;
    collateralIdentifier: string;
    ltv: number;
    lqt: number;
    amount: number;
};

export type OrderbookQuote = {
    apy: number;
    maxPrincipalAvailable: number;
    sumPrincipalAvailable: number;
};

export type OrderbookQuoteForApy = {
    collateral: string;
    maxPrincipalAvailable: number;
    sumPrincipalAvailable: number;
};

export type MaxQuote = {
    apy: number;
    lendingStrategyAddress: string;
    ltv: number;
    liquidationThreshold: number;
    principalAvailable: number;
    collateralMint: string;
};

export type BestQuote = {
    duration: number;
    durationType: DurationType;
    lendingStrategyAddress: string;
    marketInformation: string;
    apy: number;
    principalAvailable: number;
    ltv: number;
    liquidationThreshold: number;
    collateralMint: string; // added in FE
};

export type BestSellQuote = BestQuote;
export interface BorrowCap {
    principalMint: string;
    perLoan: number;
    global: number;
}

export type PrincipalMarketStats = {
    principalUtilized: number;
    principalOriginated: number;
    principalPrice: number;
    wAvgBorrowApy: number | null;
    wAvgLendApy: number | null;
    durationToWAvgBorrowApy: DurationWAvgBorrowApy[];
};

export type DurationWAvgBorrowApy = {
    duration: number;
    durationType: DurationType;
    wAvgApy: number;
    /** Lamports on the wire, converted to UI token amount by `convertPrincipalMarketStats`. */
    principalDue: number;
};

export type RatesHistoryParams = {
    principalMints: string[];
    timeLookback: number;
    onlyLoopscale?: boolean;
};
export enum PrincipalCollateralRateHistorySortType {
    PrincipalOutstanding = 0
}

export type PrincipalCollateralBorrowRateQuery = {
    principalMint: string;
    collateralMints?: string[]; // Explicit mint filter. If empty/omitted, all mints for this principal are candidates.
    timeLookback?: number;

    limit?: number; // Max collateral mints to return. Defaults to 10, capped at 20.
    sortType?: PrincipalCollateralRateHistorySortType;
    sortDirection?: SortDirection;
    sampleIntervalSecs?: number; // Server-side downsampling. Omit for full resolution.
};

export type HistoricalPrincipalCollateralBorrowRate = {
    avgBorrowRate: number | null;
    totalPrincipalDeployedUsd: number | null;
    wAvgActiveBorrowRate: number | null;
    wAvgBorrowRate: number | null;
    collateralYield: number | null;
    wAvgApy: number | null;
    collateralDepositedUsd: number | null;
    timestamp: number;
};

export type PrincipalCollateralRateHistoryResponse = {
    perCollateral: Record<string, HistoricalPrincipalCollateralBorrowRate[]>;
    /**
     * Cross-collateral aggregate for this principal. Aggregates ALL collaterals — independent
     * of the request's `collateralMints` / `limit`, so the global line stays stable as the user
     * filters per-collateral lines. Rate fields are weighted by `totalPrincipalDeployedUsd`;
     * USD totals are sums. `wAvgApy` and `collateralYield` are always `null` on global rows
     * (per-pair concepts that don't aggregate cleanly).
     */
    global: HistoricalPrincipalCollateralBorrowRate[];
};

export type CollateralYieldHistoryQuery = {
    startTime: number;
    endTime: number;
    collateralMints: string[];
};

export enum RateMarketType {
    Loopscale,
    KaminoMain,
    MarginFi,
    Solend
}

export type HistoricalMarketRate = {
    id: number;
    marketType: RateMarketType;
    principalMint: "string";
    timestamp: number;
    borrowApy: number;
    lendApy: number;
    duration: number;
    durationType: DurationType;
};

export interface CollateralYieldHistory {
    id: number;
    collateralMint: string;
    apy: number;
    timestamp: number;
}

export type EstimatedRefinanceInfo = {
    collateralMint: string;
    ltv: number;
    liquidationThreshold: number;
};

export type RefinanceOption = {
    loanEndDate: number;
    bestQuote: BestQuote | undefined;
    totalDebt: number;
    strategyDuration: StrategyDuration;
    healthRatio: number;
    ltv: number;
};

export interface AdditionalPrincipalLimit {
    additionalBorrowLimit: number;
    additionalBorrowLimitFromQuote: number;
    maxAdditionalBorrowFromLtv: number;
}

export interface IncreasePrincipalQuote {
    apy: number;
    amountLeft: number;
    strategyAddress: string;
    ltv: number;
    liquidationThreshold: number;
}

export interface RateStats {
    loopRates: Record<RateMarketType, HistoricalMarketRate[]>;
    vaultRates: Record<RateMarketType, HistoricalMarketRate[]>;
}

export interface EcosystemStats {
    totalDeposits: number;
    partnerCount: number;
}

export interface EarnStats {
    tvlUsd: number;
}

export interface PlatformTotals {
    totalDepositsUsd: number;
    activeLoansUsd: number;
    vaultDepositsUsd: number;
    totalCollateralUsd: number;
    totalLoansCount: number;
}

export interface UnifiedStatsRequest {
    principalMints?: string[];
    force?: boolean;
    includeStrategy?: boolean;
    includePrincipal?: boolean;
    includeCollateral?: boolean;
    includeBorrowCaps?: boolean;
    ratesFilter?: RatesHistoryParams;
    includePrincipalToCollateralDepositedUsd?: boolean;
    includeEcosystem?: boolean;
    includeEarn?: boolean;
    includeTotals?: boolean;
}

export interface UnifiedStatsResponse {
    strategy?: Record<string, PrincipalMarketStrategyStats>;
    principal?: Record<string, PrincipalMarketStats>;
    collateral?: Record<string, number>;
    rates?: RateStats;
    principalToDepositedCollateralUsd?: Record<string, Record<string, number>>;
    borrowCaps?: Record<string, BorrowCap>;
    ecosystem?: EcosystemStats;
    earn?: EarnStats;
    totals?: PlatformTotals;
}

export interface RawUnifiedStatsResponse {
    strategy?: Record<string, RawPrincipalMarketStrategyStats>;
    principal?: Record<string, PrincipalMarketStats>;
    collateral?: Record<string, number>;
    rates?: RateStats;
    principalToDepositedCollateralUsd?: Record<string, Record<string, number>>;
    borrowCaps?: Record<string, BorrowCap>;
    ecosystem?: EcosystemStats;
    earn?: EarnStats;
    totals?: PlatformTotals;
}

export interface MarketAssetView {
    market: ParsedMarketInformation;
    asset: MarketInformationAssetData;
    customOracles: MarketInformationAssetData[];
}
